import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Award, Users, Target, TrendingUp, BookOpen, Shield, Calendar, ArrowLeft, Wrench, Settings, BarChart3, Briefcase } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { useNavigate } from 'react-router-dom';

export const TotalProductiveMaintenance = () => {
  const navigate = useNavigate();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "Total Productive Maintenance Course",
    "description": "Enroll in Total Productive Maintenance (TPM) training to improve equipment reliability, OEE, and operational efficiency with expert-led certification.",
    "provider": {
      "@type": "Organization",
      "name": "ISDE Academy",
      "sameAs": "https://isdeacademy.com"
    },
    "coursePrerequisites": "Basic understanding of manufacturing or industrial operations",
    "educationalLevel": "Beginner to Advanced",
    "availableLanguage": "English",
    "inLanguage": "en",
    "audience": {
      "@type": "Audience",
      "audienceType": "Maintenance Engineers, Production Managers, Plant Managers, Quality Professionals, Industrial Engineers, Operations Managers"
    },
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "Blended",
      "duration": "P3D",
      "location": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "India"
        }
      }
    },
    "teaches": [
      "TPM concepts and framework",
      "Overall Equipment Effectiveness (OEE)",
      "Autonomous maintenance",
      "Preventive and predictive maintenance",
      "Root cause analysis",
      "Equipment loss analysis"
    ],
    "occupationalCredentialAwarded": {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "Professional Certification",
      "name": "TPM Practitioner Certification"
    }
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Who should attend a Total Productive Maintenance Course?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "A TPM course is suitable for Maintenance engineers and technicians, Production managers and supervisors, Plant and factory managers, Quality and process improvement professionals, Industrial engineers, Operations and reliability managers, Lean Six Sigma practitioners, and Manufacturing executives."
        }
      },
      {
        "@type": "Question",
        "name": "What are the benefits of Total Productive Maintenance?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Benefits include Increased Productivity, Reduced Maintenance Costs, Improved Safety and Compliance, Professional Skill Development, and Strong ROI for Organizations through measurable financial and operational benefits."
        }
      },
      {
        "@type": "Question",
        "name": "What career opportunities are available after TPM training?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Professionals trained in TPM can pursue roles such as Maintenance Manager, Reliability Engineer, TPM Coordinator, Continuous Improvement Manager, Plant Operations Manager, and Lean Manufacturing Consultant."
        }
      }
    ]
  };

  const tpmTrainsTo = [
    "Improve Overall Equipment Effectiveness (OEE)",
    "Reduce equipment downtime and failures",
    "Enhance productivity and quality",
    "Foster teamwork between maintenance and production teams"
  ];

  const importancePoints = [
    {
      title: "Reduces Unplanned Downtime",
      description: "TPM focuses on proactive and preventive maintenance, significantly reducing unexpected equipment failures that disrupt production."
    },
    {
      title: "Improves Overall Equipment Effectiveness (OEE)",
      description: "A key objective of TPM is maximizing OEE by addressing losses related to availability, performance, and quality."
    },
    {
      title: "Enhances Equipment Life",
      description: "Proper maintenance and early detection of issues help extend the lifespan of machinery and assets."
    },
    {
      title: "Builds a Culture of Ownership",
      description: "Operators are trained to take responsibility for routine maintenance, inspections, and cleanliness, creating a sense of ownership and accountability."
    },
    {
      title: "Supports Lean Manufacturing and Six Sigma",
      description: "TPM complements Lean and Six Sigma initiatives by eliminating waste, improving process reliability, and stabilizing operations."
    }
  ];

  const eligibility = [
    "Basic understanding of manufacturing or industrial operations",
    "Experience in production, maintenance, or engineering (preferred but not mandatory)",
    "Interest in operational excellence and continuous improvement"
  ];

  const targetAudience = [
    "Maintenance engineers and technicians",
    "Production managers and supervisors",
    "Plant and factory managers",
    "Quality and process improvement professionals",
    "Industrial engineers",
    "Operations and reliability managers",
    "Lean Six Sigma practitioners",
    "Manufacturing executives"
  ];

  const keyComponents = [
    {
      title: "TPM Concepts and Framework",
      description: "Participants learn the philosophy, history, and principles of TPM and how it integrates with business goals."
    },
    {
      title: "Equipment Loss Analysis",
      description: "Training covers the six big losses: Equipment breakdowns, Setup and adjustment losses, Minor stoppages, Reduced speed, Quality defects, and Startup losses."
    },
    {
      title: "OEE Measurement and Improvement",
      description: "Participants learn how to calculate, analyze, and improve Overall Equipment Effectiveness using real-world data."
    },
    {
      title: "Autonomous Maintenance Implementation",
      description: "The course explains step-by-step implementation of autonomous maintenance, including standardization and operator training."
    },
    {
      title: "Preventive and Predictive Maintenance",
      description: "Learners understand how to design maintenance schedules, condition monitoring systems, and predictive maintenance strategies."
    },
    {
      title: "Root Cause Analysis and Problem Solving",
      description: "Tools such as Root Cause Analysis (RCA), Fishbone diagrams, and 5 Why analysis are taught to identify and eliminate recurring equipment issues."
    }
  ];

  const sixBigLosses = [
    "Equipment breakdowns",
    "Setup and adjustment losses",
    "Minor stoppages",
    "Reduced speed",
    "Quality defects",
    "Startup losses"
  ];

  const rcaTools = [
    "Root Cause Analysis (RCA)",
    "Fishbone diagrams",
    "5 Why analysis"
  ];

  const benefits = [
    {
      title: "Increased Productivity",
      description: "TPM helps organizations achieve stable, efficient, and high-output operations."
    },
    {
      title: "Reduced Maintenance Costs",
      description: "Preventive maintenance and early detection reduce expensive emergency repairs."
    },
    {
      title: "Improved Safety and Compliance",
      description: "Well-maintained equipment reduces workplace accidents and safety risks."
    },
    {
      title: "Professional Skill Development",
      description: "Participants gain valuable skills in equipment management, reliability, and continuous improvement."
    },
    {
      title: "Strong ROI for Organizations",
      description: "TPM initiatives often deliver measurable financial and operational benefits within months of implementation."
    }
  ];

  const providerCriteria = [
    "Industry-experienced trainers",
    "Practical, implementation-focused curriculum",
    "Real-world case studies",
    "Post-training implementation support",
    "Customization based on industry and plant needs"
  ];

  const careerOpportunities = [
    "Maintenance Manager",
    "Reliability Engineer",
    "TPM Coordinator",
    "Continuous Improvement Manager",
    "Plant Operations Manager",
    "Lean Manufacturing Consultant"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Total Productive Maintenance Course | TPM Training & Certification"
        description="Enroll in Total Productive Maintenance (TPM) training to improve equipment reliability, OEE, and operational efficiency with expert-led certification."
        keywords="Total Productive Maintenance Course, TPM training, TPM certification, OEE improvement, equipment maintenance, preventive maintenance, autonomous maintenance, reliability engineering, manufacturing excellence, plant maintenance training, TPM training India, TPM course Chennai, TPM course Bangalore, TPM certification India"
        canonical="/total-productive-maintenance-course"
        structuredData={[structuredData, faqStructuredData]}
        breadcrumbs={[
          { name: 'Training', path: '/training' },
          { name: 'Total Productive Maintenance', path: '/total-productive-maintenance-course' }
        ]}
      />

      {/* Hero Section with H1 */}
      <header className="bg-gradient-to-r from-orange-600 to-amber-600 text-white py-20 mt-20">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Total Productive Maintenance Course
            </h1>
          </motion.div>
        </div>
      </header>

      {/* Back Button */}
      <nav className="bg-gray-50 py-6" aria-label="Breadcrumb">
        <div className="max-w-7xl mx-auto px-4">
          <Button
            onClick={() => navigate('/training/iso-training-services')}
            variant="outline"
            className="flex items-center gap-2 hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to ISO Training Services
          </Button>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 py-12">
        <article>
          {/* Introduction Section */}
          <section className="mb-16">
            <Card>
              <CardContent className="p-8">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  In today's highly competitive manufacturing and industrial environment, organizations can no longer afford unplanned downtime, frequent breakdowns, or inefficient equipment usage. To remain productive, cost-effective, and globally competitive, companies are increasingly adopting <strong>Total Productive Maintenance (TPM)</strong> as a core operational strategy.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  A <strong>Total Productive Maintenance Course</strong> equips professionals with the knowledge, tools, and mindset required to maximize equipment effectiveness, eliminate losses, and build a culture of proactive maintenance. TPM is not just a maintenance technique—it is a holistic approach that involves everyone in the organization, from operators to top management.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Total Productive Maintenance (TPM) is a systematic approach to equipment maintenance that aims to achieve <strong>zero breakdowns, zero defects, and zero accidents</strong>. Unlike traditional maintenance practices, TPM emphasizes operator involvement, preventive maintenance, and continuous improvement.
                </p>
                <p className="text-lg text-gray-700 mb-4">A Total Productive Maintenance Course trains individuals to:</p>
                <ul className="grid md:grid-cols-2 gap-3 list-none">
                  {tpmTrainsTo.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-orange-600 flex-shrink-0 mt-1" aria-hidden="true" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-lg text-gray-700 mt-6">
                  TPM originated in Japan and has become a globally recognized best practice in manufacturing, automotive, pharmaceuticals, FMCG, food processing, and process industries.
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Importance Section - H2 */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                <TrendingUp className="h-8 w-8 text-orange-600" aria-hidden="true" />
                Importance of a Total Productive Maintenance Course
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {importancePoints.map((point, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="bg-orange-100 p-3 rounded-lg flex-shrink-0">
                          <span className="text-orange-600 font-bold text-lg" aria-hidden="true">{index + 1}</span>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-lg font-bold mb-2 text-gray-900">{point.title}</h4>
                          <p className="text-gray-700 text-sm">{point.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          </section>

          {/* Eligibility Section - H2 */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                <Award className="h-8 w-8 text-orange-600" aria-hidden="true" />
                Eligibility for a Total Productive Maintenance Course
              </h2>
              <Card>
                <CardContent className="p-8">
                  <p className="text-lg text-gray-700 mb-6">Most TPM courses have flexible eligibility requirements, making them accessible to a wide audience. Typical prerequisites include:</p>
                  <ul className="space-y-3 list-none mb-6">
                    {eligibility.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-6 w-6 text-orange-600 flex-shrink-0 mt-1" aria-hidden="true" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-lg text-gray-700 font-semibold">
                    No prior TPM experience is required for foundation-level courses.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </section>

          {/* Who Should Attend - H3 */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                <Users className="h-7 w-7 text-orange-600" aria-hidden="true" />
                Who Should Attend a Total Productive Maintenance Course?
              </h3>
              <Card>
                <CardContent className="p-8">
                  <p className="text-lg text-gray-700 mb-6">A TPM course is suitable for professionals across multiple roles, including:</p>
                  <ul className="grid md:grid-cols-2 gap-4 list-none">
                    {targetAudience.map((audience, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-6 w-6 text-orange-600 flex-shrink-0 mt-1" aria-hidden="true" />
                        <span className="text-gray-700">{audience}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-lg text-gray-700 mt-6 font-semibold">
                    Both entry-level and experienced professionals benefit from TPM training.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </section>

          {/* Key Components - H3 */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                <Settings className="h-7 w-7 text-orange-600" aria-hidden="true" />
                Key Components of a Total Productive Maintenance Course
              </h3>
              <div className="space-y-4">
                {keyComponents.map((component, index) => (
                  <Card key={index} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="bg-orange-100 p-3 rounded-lg flex-shrink-0">
                          <span className="text-orange-600 font-bold text-lg" aria-hidden="true">{index + 1}</span>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-xl font-bold mb-2 text-gray-900">{component.title}</h4>
                          <p className="text-gray-700">{component.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          </section>

          {/* Benefits - H3 */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold mb-8 text-gray-900 flex items-center gap-3">
                <BarChart3 className="h-7 w-7 text-orange-600" aria-hidden="true" />
                Benefits of Enrolling in a Total Productive Maintenance Course
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {benefits.map((benefit, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow bg-gradient-to-br from-orange-50 to-amber-50">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="bg-orange-100 p-3 rounded-lg flex-shrink-0">
                          <span className="text-orange-600 font-bold text-lg" aria-hidden="true">{index + 1}</span>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-lg font-bold mb-2 text-gray-900">{benefit.title}</h4>
                          <p className="text-gray-700 text-sm">{benefit.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          </section>

          {/* Choosing Provider - H4 */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h4 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                <BookOpen className="h-7 w-7 text-orange-600" aria-hidden="true" />
                Choosing the Right Total Productive Maintenance Course Provider
              </h4>
              <Card>
                <CardContent className="p-8">
                  <p className="text-lg text-gray-700 mb-6">When selecting a TPM training provider, look for:</p>
                  <ul className="grid md:grid-cols-2 gap-4 list-none">
                    {providerCriteria.map((criteria, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-6 w-6 text-orange-600 flex-shrink-0 mt-1" aria-hidden="true" />
                        <span className="text-gray-700">{criteria}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-lg text-gray-700 mt-6 font-semibold">
                    A credible provider ensures the training delivers actionable results, not just theory.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </section>

          {/* Career Opportunities - H4 */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h4 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                <Briefcase className="h-7 w-7 text-orange-600" aria-hidden="true" />
                Career Opportunities After a Total Productive Maintenance Course
              </h4>
              <Card className="bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200">
                <CardContent className="p-8">
                  <p className="text-lg text-gray-700 mb-6">Professionals trained in TPM can pursue roles such as:</p>
                  <ul className="grid md:grid-cols-3 gap-4 mb-6 list-none">
                    {careerOpportunities.map((opportunity, index) => (
                      <li key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm">
                        <CheckCircle className="h-6 w-6 text-orange-600 flex-shrink-0 mt-1" aria-hidden="true" />
                        <span className="text-gray-700 font-semibold">{opportunity}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-lg text-gray-700 font-semibold">
                    TPM skills are highly valued across manufacturing, pharmaceuticals, automotive, FMCG, and heavy industries.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </section>

          {/* FAQ Section */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gray-900 flex items-center gap-3">
                <Briefcase className="h-8 w-8 text-orange-600" aria-hidden="true" />
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqStructuredData.mainEntity.map((faq, index) => (
                  <Card key={index} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.name}</h3>
                      <p className="text-gray-700 leading-relaxed">{faq.acceptedAnswer.text}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          </section>
        </article>

        {/* CTA Section */}
        <aside>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Card className="bg-gradient-to-r from-orange-600 to-amber-600 text-white">
              <CardContent className="p-12 text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Ready to Master Total Productive Maintenance?
                </h2>
                <p className="text-xl text-orange-100 mb-8 max-w-3xl mx-auto">
                  Join manufacturing and maintenance professionals who have transformed their operations with our comprehensive TPM training programs.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Button
                    onClick={() => navigate('/training-calendar')}
                    className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-6 text-lg"
                    size="lg"
                  >
                    <Calendar className="h-5 w-5 mr-2" aria-hidden="true" />
                    View Training Schedule
                  </Button>
                  <Button
                    onClick={() => navigate('/contact')}
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-orange-600 px-8 py-6 text-lg"
                    size="lg"
                  >
                    Request More Information
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </aside>
      </main>
    </div>
  );
};
